/**
 * Sentry initialization for the frontend (React/Vite).
 *
 * Init runs as the first runtime statement in `main.tsx` so auto-captured
 * errors include early init failures.
 *
 * Source maps are uploaded to Sentry by `@sentry/vite-plugin` at build
 * time (configured in `vite.config.mts`); without this, prod stack traces
 * are minified gibberish.
 *
 * Kill-switch: `VITE_OBSERVABILITY_ENABLED=false` in env skips init.
 */

declare global {
  interface ImportMetaEnv {
    readonly VITE_GIT_SHA?: string;
    readonly VITE_SENTRY_DSN_FRONTEND?: string;
    readonly VITE_OBSERVABILITY_ENABLED?: string;
    // MODE is already declared by vite/client — don't redeclare (TS2687).
  }
}

export function initSentry(): void {
  if (import.meta.env.VITE_OBSERVABILITY_ENABLED === "false") {
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN_FRONTEND;
  if (!dsn) {
    // No DSN configured — silently no-op (don't log, no need to spam console)
    return;
  }

  const environment = import.meta.env.MODE;
  if (environment !== "production" && environment !== "staging") {
    return;
  }

  const tracesSampleRate = environment === "staging" ? 1.0 : 0.1;

  // Lazy import so the bundle skips Sentry when disabled / no DSN.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  import("@sentry/react")
    .then((Sentry) => {
      Sentry.init({
        dsn,
        environment,
        release: import.meta.env.VITE_GIT_SHA ?? "dev",
        tracesSampleRate,
        sendDefaultPii: false,
      });
    })
    .catch((err) => {
      // Never throw from observability init.
      console.warn("Sentry init failed:", err);
    });
}

/**
 * Set the Sentry user-context using the public ID (never internal UUID).
 * Call from `AuthContext` after sign-in resolves the user.
 */
export function setSentryUser(userPublicId: string | null): void {
  if (import.meta.env.VITE_OBSERVABILITY_ENABLED === "false") return;
  import("@sentry/react")
    .then((Sentry) => {
      if (userPublicId) {
        Sentry.setUser({ id: userPublicId });
      } else {
        Sentry.setUser(null);
      }
    })
    .catch(() => {
      /* swallow — observability mishap can't break auth */
    });
}

/**
 * Report a handled failure to Sentry.
 *
 * The two existing call sites (`AppErrorBoundary`, `AnswerSections`) inline
 * this same dynamic-import dance; this is that pattern with a name, for paths
 * that fail *without* crashing a component tree.
 *
 * It exists for a specific blind spot: the Google Sheets export runs
 * browser→Drive and never touches our servers, so a broken JavaScript origin
 * on the OAuth client, or a Drive 4xx, produces no log line anywhere in our
 * infrastructure. Without this we would learn about a dead export path from a
 * customer, not from a dashboard.
 *
 * `context` is a short mechanical label ("sheets-export:upload"), never user
 * data — the exported rows are the customer's own numbers and must not leave
 * with an error report.
 *
 * Never throws, never rejects: an observability mishap must not become the
 * user's problem.
 */
export function reportError(error: unknown, context: string): void {
  if (import.meta.env.VITE_OBSERVABILITY_ENABLED === "false") return;
  import("@sentry/react")
    .then((Sentry) => {
      Sentry.captureException(error, { tags: { verity_context: context } });
    })
    .catch(() => {
      /* swallow — reporting a failure must not create a second one */
    });
}
